import { forwardRef, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { isNil } from "ramda";
import { Slide, Typography } from "@mui/material";

import AWSRepository from "repositories/AWSRepository";
import { dataURItoBlob } from "utils/imageUtils";
import {
  useScreen,
  useTemplateGroup,
  useCasting,
  usePeople,
  useSaveMakeAndGo,
  useShares,
  useRouter,
  useAssetUpload,
} from "hooks";
import PersonPresenter from "presenters/PersonPresenter";
import TemplateGroupPresenter from "presenters/TemplateGroupPresenter";
import DisplayPerson from "components/DisplayPerson";
import { DATE_TYPE } from "enums";
import { trackCalendarDiscovery } from "tracking/google/analytics";
import { trackCordialMakeCreated } from "utils/cordialUtils";
import { shouldAddNextYear } from "utils/dateTimeUtils";
import { capitalize } from "utils/stringUtils";
import selectedCastNumber from "utils/selectedCastNumber";
import { jjLogger } from "utils/logUtils";
import { appRoutes } from "routes";

import {
  StyledDialog,
  StyledPaper,
  Container,
  StyledSpinningCloseButton,
  Info,
  Actions,
  Video,
  PlayerContainer,
  PlayerActions,
  TextContent,
  Title,
  Text,
  Buttons,
  ButtonEdit,
  ButtonProgress,
  ButtonOrder,
  Schedule,
} from "./styles";

const CAST_COUNT = 1;

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const CalendarDetailModal = ({ person, dateType, dateToCelebrate, onClose, items }) => {
  const { isDesktop } = useScreen();
  const { setDateToShare } = useShares();
  const { push } = useRouter();
  const { uploadThumbAssets } = useAssetUpload();
  const { loadTemplateGroup, templateGroup } = useTemplateGroup();
  const { card, init, cleanup, CONSTANTS, castFace, getData } = useCasting();
  const { updatePerson, peopleToUpdate, resetPeopleToUpdate, loadPeopleToCast } = usePeople();

  const [templateGroupsInfo, setTemplateGroupsInfo] = useState([]);
  const [currentTemplateGroupInfo, setCurrentTemplateGroupInfo] = useState({});
  const [isChangeVideo, setisChangeVideo] = useState(false);
  const [disabledButton, setDisabledButton] = useState(false);

  const face = PersonPresenter.defaultHead(person);
  const faces = [face];
  const src = TemplateGroupPresenter.previewVideo(currentTemplateGroupInfo);
  const celebrationDate = shouldAddNextYear(dateToCelebrate);

  const saveAndMakeSuccess = (currentUser, make) => {
    setDateToShare(celebrationDate.format("YYYY-MM-DD"));
    trackCordialMakeCreated(make, templateGroup);
  };
  const { saveMakeAndGo } = useSaveMakeAndGo(saveAndMakeSuccess, templateGroup);

  const shuffleTemplate = () => {
    const allTemplates = isNil(templateGroupsInfo) ? [] : [...templateGroupsInfo];

    if (currentTemplateGroupInfo) {
      const currentTemplateIndex = allTemplates.findIndex((template) => template.id === currentTemplateGroupInfo.id);
      allTemplates.splice(currentTemplateIndex, 1);
    }

    const randomTemplate = allTemplates[Math.floor(Math.random() * allTemplates.length)];
    setCurrentTemplateGroupInfo(randomTemplate);
  };

  const handleShuffleTemplate = () => {
    shuffleTemplate();
    trackCalendarDiscovery("Shuffle");
  };

  const handleStoreMake = async () => {
    const { roles, makeThumbUrl, shareThumbUrl, templateId } = getData();
    const actions = uploadThumbAssets([
      AWSRepository.presignedUploadToS3({ method: "make_thumb" }, dataURItoBlob(makeThumbUrl)),
      AWSRepository.presignedUploadToS3({ method: "share_thumb" }, dataURItoBlob(shareThumbUrl)),
    ]);

    return actions.then((results) => {
      const [makeThumb, shareThumb] = results;
      peopleToUpdate.map(updatePerson);
      resetPeopleToUpdate();

      return saveMakeAndGo({ roles, templateId, makeThumb, shareThumb });
    });
  };

  const handleCloseModal = () => {
    if (disabledButton) return;
    onClose();
  };

  const handleSchedule = () => {
    loadTemplateGroup(currentTemplateGroupInfo.id);
    setDisabledButton(true);
    trackCalendarDiscovery("Schedule now");
  };

  const handleEdit = () => {
    loadPeopleToCast([person]);
    selectedCastNumber.set(CAST_COUNT);
    trackCalendarDiscovery("Edit");
    push(appRoutes.newCastPath(currentTemplateGroupInfo.id));
  };

  const TitleComponent = () => {
    const dateFormat = "MMMM D, YYYY";
    const title = `${capitalize(dateType)} Ecard`;
    const subtitle = `Schedule it now for ${celebrationDate.format(dateFormat)}`;

    return (
      <TextContent>
        <Text variant="h3">Send a</Text>
        <Title variant="h3">{title}</Title>
        <Typography variant="h3">{subtitle}</Typography>
      </TextContent>
    );
  };

  useEffect(() => {
    if (items.length > 0) setTemplateGroupsInfo(items);
  }, [items]);

  useEffect(shuffleTemplate, [templateGroupsInfo]);

  useEffect(() => {
    if (src != null) {
      setisChangeVideo(true);
      setTimeout(() => {
        setisChangeVideo(false);
      }, 100);
    }
  }, [currentTemplateGroupInfo]);

  useEffect(() => {
    if (isNil(templateGroup) || card) return undefined;
    init(faces, templateGroup);
    return () => cleanup();
  }, [templateGroup]);

  useEffect(() => {
    if (card) {
      jjLogger.log(`CalendarDetailModal: Calling castFace()`);
      castFace(face).then(() => {
        handleStoreMake();
      });
    }
  }, [card]);

  return (
    <StyledDialog
      dateType={dateType}
      maxWidth={isDesktop ? "lg" : ""}
      open
      onClose={handleCloseModal}
      TransitionComponent={Transition}
      PaperComponent={StyledPaper}
    >
      <div hidden id={CONSTANTS.canvasId} />
      <StyledSpinningCloseButton data-testid="calendarDetailModal-closeButton" onClick={handleCloseModal} />
      <Container>
        <Info>
          <DisplayPerson
            showLabel
            person={person}
            noOpacity
            showBadge={false}
            hasPartyHat={dateType === DATE_TYPE.birthday}
            dateType={dateType}
          />
          <TitleComponent />
        </Info>

        <Actions>
          <PlayerContainer>
            <Video
              src={src}
              poster={TemplateGroupPresenter.posterUrl(currentTemplateGroupInfo)}
              autoplay={false}
              joinable={false}
              controls
              isChangeVideo={isChangeVideo}
              aspectRatio="16/9"
            />
            <PlayerActions>
              <ButtonEdit onClick={handleEdit} variant="contained" size="small" disabled={disabledButton}>
                Edit
              </ButtonEdit>
              <Typography variant="small">
                {!isNil(currentTemplateGroupInfo) &&
                  `${TemplateGroupPresenter.firstCastNum(currentTemplateGroupInfo)} Person`}
              </Typography>
            </PlayerActions>
          </PlayerContainer>
          <Buttons>
            <ButtonOrder
              variant="outlined"
              color="primary"
              size={isDesktop ? "large" : "medium"}
              onClick={handleShuffleTemplate}
              disabled={disabledButton}
            >
              Shuffle
            </ButtonOrder>
            <Schedule
              variant="contained"
              color="primary"
              size={isDesktop ? "large" : "medium"}
              onClick={handleSchedule}
              disabled={disabledButton}
            >
              Schedule now!
              {disabledButton && <ButtonProgress size={24} />}
            </Schedule>
          </Buttons>
        </Actions>
      </Container>
    </StyledDialog>
  );
};

CalendarDetailModal.propTypes = {
  person: PersonPresenter.shape().isRequired,
  dateType: PropTypes.string.isRequired,
  dateToCelebrate: PropTypes.string,
  onClose: PropTypes.func,
  items: PropTypes.arrayOf(PropTypes.shape()),
};

export default CalendarDetailModal;
